import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { useDispatch, useSelector } from "react-redux";

import {
  getCopilotConversations,
  updateCopilotConversation,
} from "slices/copilot";
import { buildConversationFilter } from "./conversationFilter";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Lists the current user's previous Copilot conversations.
 *
 * Holds no chat state — the parent owns which conversation is active and passes
 * it in as `activeId`, and is told about selections via `onSelect`.
 */
const ConversationHistoryPanel = ({ activeId, onSelect }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const conversations = useSelector(
    (state) => state.copilot.conversation.data.list
  );
  const nextPage = useSelector(
    (state) => state.copilot.conversation.data.nextPage
  );
  const loading = useSelector((state) => state.copilot.conversation.loading);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const listRef = useRef(null);

  // Debounce the search box so each keystroke does not hit the API.
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchInput),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filter = useMemo(
    () => buildConversationFilter(debouncedSearch),
    [debouncedSearch]
  );

  // Fetch page 0 whenever the filter changes.
  useEffect(() => {
    dispatch(
      getCopilotConversations({
        search: { filter, sort: "-modifiedAt", page: 0, page_size: PAGE_SIZE },
        append: false,
      })
    );
  }, [dispatch, filter]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !nextPage) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) {
      dispatch(
        getCopilotConversations({
          search: {
            filter,
            sort: "-modifiedAt",
            page: nextPage,
            page_size: PAGE_SIZE,
          },
          append: true,
        })
      );
    }
  };

  const startRename = (event, conversation) => {
    event.stopPropagation();
    setRenamingId(conversation.id);
    setRenameValue(conversation.name || "");
  };

  const cancelRename = (event) => {
    if (event) event.stopPropagation();
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = (event, conversationId) => {
    if (event) event.stopPropagation();

    const name = renameValue.trim();
    if (name) {
      dispatch(
        updateCopilotConversation({
          copilotConversation: { id: conversationId, name },
        })
      );
    }

    setRenamingId(null);
    setRenameValue("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search chats"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {conversations.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ p: 2, color: theme.palette.text.secondary }}
          >
            No previous chats yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {conversations.map((conversation) => (
              <ListItemButton
                key={conversation.id}
                selected={conversation.id === activeId}
                onClick={() => onSelect(conversation.id)}
                sx={{ pr: 1 }}
              >
                {renamingId === conversation.id ? (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={renameValue}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          commitRename(event, conversation.id);
                        }
                        if (event.key === "Escape") {
                          cancelRename(event);
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Save name"
                      onClick={(event) => commitRename(event, conversation.id)}
                    >
                      <CheckOutlined fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Cancel rename"
                      onClick={cancelRename}
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <ListItemText
                      primary={conversation.name}
                      primaryTypographyProps={{
                        noWrap: true,
                        variant: "body2",
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label={`Rename ${conversation.name}`}
                      onClick={(event) => startRename(event, conversation)}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </>
                )}
              </ListItemButton>
            ))}
          </List>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConversationHistoryPanel;
